import { useRouter } from "next/router";
import { TriangleAlert } from "lucide-react";
import { JhText } from "designPackages/textPackage";
import { Routes } from "routes/RouteConstants";
import LogService from "services/LogService";
import CREDIT_EVENTS from "utils/CreditEvents";
import { useIsMobile } from "hooks/useDeviceType";
import { JhButton } from "designPackages/buttonPackage";

interface CreditProfileData {
  isCreditBetaUser?: boolean;
  creditsDateOfExpiry?: Date | null;
  creditsGettingExpired?: number | null;
  showExpiringCredits?: boolean;
  differenceInDays?: number;
  [key: string]: any;
}

interface TransactionHistoryV2BannerProps {
  creditProfileData?: CreditProfileData;
}

const TransactionHistoryV2Banner = ({ creditProfileData = {} }: TransactionHistoryV2BannerProps) => {
  const router = useRouter();
  const isMobile = useIsMobile();
  const { tabId = "ALL_TRANSACTIONS" } = router.query;

  const {
    isCreditBetaUser,
    creditsDateOfExpiry: expiryDate = null,
    creditsGettingExpired: expiringCredits = null,
    showExpiringCredits,
    differenceInDays
  } = creditProfileData;

  const isReservedTab = tabId === "CREDITS_ON_HOLD";


  const handlePostNowClick = () => {
    LogService.log(CREDIT_EVENTS.EXPIRING_CREDIT_POST_JOB_CLICKED);
    router.push(Routes.JOB_POSTING_FORM);
  };

  // Show reserved credits banner (matches Figma design)
  if (isReservedTab) {
    return (
      <div id="th-v2-banner-reserved-wrapper" className="mx-auto w-full">
        <div id="th-v2-banner-reserved-container" className={`bg-white border border-slate-200 rounded-md p-3 flex items-start gap-2.5 flex-col md:flex-row`}>
          {/* Icon container with orange background */}
          <div id="th-v2-banner-reserved-icon-container" className="bg-orange-50 rounded p-2 flex items-center justify-center flex-shrink-0">
            <TriangleAlert size={16} className="text-orange-500" />
          </div>

          {/* Text content */}
          <div id="th-v2-banner-reserved-text-container" className="flex-1 min-w-0">
            <JhText variant="caption2" color="muted-foreground">
              Your credits are temporarily reserved while we review your recent job posting. Credits will be consumed only when your job is made active. If the job gets rejected, these credits will be automatically made available for use.
            </JhText>
          </div>
        </div>
      </div>
    );
  }

  if (!isCreditBetaUser || !showExpiringCredits) return null;

  // Show expiring credits banner (existing design)
  return (
    <div id="th-v2-banner-expiring-container" className={`w-full bg-warning border border-border rounded-md p-3 flex items-start gap-2.5 flex-row-reverse md:flex-row`}>
      <span className="w-11 h-11 bg-background rounded-md flex items-center justify-center">
        <TriangleAlert size={24} className="text-warning-foreground" />
      </span>
      <div id="th-v2-banner-expiring-content" className='flex-1 flex flex-col gap-2.5 md:flex-row md:justify-between md:items-center'>
        <div id="th-v2-banner-expiring-text-container" className="flex-col gap-1">
          <JhText variant="subtitle3">
            {expiringCredits} credits expire in {differenceInDays} day{differenceInDays > 1 ? "s" : ""}
          </JhText>
          <JhText variant="body2" color="muted-foreground">
            Use credits before they expire {!isMobile && "- post jobs now before they go waste!"}
          </JhText>
        </div>
        <JhButton variant="outline" size="sm" onClick={handlePostNowClick} className="max-w-fit" >Post Now</JhButton>
      </div>
    </div>
  );
};

export default TransactionHistoryV2Banner;
